import React, {useEffect, useState} from 'react';
import {Dialog, IconButton, LinearProgress, styled} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useAppProps, useSnackbar} from '../App';
import {VotePageProps} from '../tools/front-props';
import {WatchBlock, WatchBrand, WatchImg, WatchName} from './pages/vote';

const Root = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px 20px 20px 20px;
    gap: 20px;
    max-width: 460px;
`;

const Title = styled('span')`
    font-size: 16px;
`;

const StyledProgress = styled(LinearProgress)`
    width: 100%;
    height: 4px;
    margin: 20px 0;
`;

const LoadingContainer = styled('div')`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const CategoryTitle = styled('h3')`
    margin: 0 0 10px 0;
    color: #000;
    font-size: 1.5em;
`;

const WatchRoot = styled('div')`
    width: 100%;
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    color: #666;

    &:hover {
        color: #000;
    }
`;

const DialogContent = styled('div')`
    position: relative;
`;

export const VotedWatchesDialog: React.FC<{ watchIdToVote: string | undefined, onClose: () => void }> = ({
                                                                                                             watchIdToVote,
                                                                                                             onClose
                                                                                                         }) => {
    const {pageData} = useAppProps();
    const {voteCategories} = pageData as VotePageProps;
    const {showSnackbar} = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [votedWatches, setVotedWatches] = useState([] as string[]);

    useEffect(() => {
        const voteForWatch = async (watchId: string) => {
            const categoryId = voteCategories.find(it => it.watches.find(w => w.id === watchId))?.id;
            if (!categoryId)
                return;

            try {
                const response = await fetch('/api/vote', {
                    method: 'POST',
                    headers: {
                        'Content-type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({watchId: watchId}),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                setVotedWatches(data);
                setLoading(false);
            } catch (e) {
                console.error('Failed to vote for watch:', e);
                setLoading(false);
                showSnackbar('Ошибка при голосовании. Попробуйте позже.');
                onClose();
            }
        };

        if (watchIdToVote !== undefined) {
            setLoading(true);
            voteForWatch(watchIdToVote);
        }
    }, [watchIdToVote]);

    return <Dialog
        open={watchIdToVote !== undefined}
        onClose={() => {
            onClose();
        }}
    >
        <DialogContent>
            <Root>
                <CloseButton onClick={onClose} size="small">
                    <CloseIcon/>
                </CloseButton>
                {loading ?
                    <LoadingContainer>
                        <Title>Учитываем ваш голос...</Title>
                    </LoadingContainer> :
                    <>
                        <Title>
                            {votedWatches.length < 3 ? "Внимание! Согласно правилам, при определении победителей будут учитываться только голоса людей, проголосовавших не меньше, чем за 3 номинации. Пожалуйста, продолжите выбор!" : "Вы можете поменять ваш выбор в любой момент"}
                        </Title>
                        <Title>
                            Вы проголосовали за следующие модели:
                        </Title>
                        {votedWatches.map(watchId => {
                            const voteCategory = voteCategories.find(it => it.watches.find(w => w.id === watchId));
                            if (!voteCategory)
                                return null;
                            const watch = voteCategory.watches.find(w => w.id === watchId);
                            if (!watch)
                                return null;
                            return <WatchRoot key={watchId}>
                                <CategoryTitle id={voteCategory.title}>
                                    {voteCategory.title}
                                </CategoryTitle>
                                <WatchBlock key={watch.id}>
                                    <WatchImg>
                                        <img
                                            src={`${watch.images[0]}?width=500`}
                                            srcSet={`
                                        ${watch.images[0]}?width=250 250w,
                                        ${watch.images[0]}?width=500 500w,
                                        ${watch.images[0]}?width=750 750w,
                                        ${watch.images[0]}?width=1000 1000w
                                    `}
                                            sizes="(max-width: 600px) 100vw, (max-width: 1200px) 50vw,33vw"
                                            alt={watch.name}
                                            style={{width: '100%', height: 'auto'}}
                                        />
                                    </WatchImg>
                                    <WatchBrand>
                                        {watch.brand}
                                    </WatchBrand>
                                    <WatchName>
                                        {watch.name}
                                    </WatchName>
                                </WatchBlock>
                            </WatchRoot>
                        })}
                    </>}
            </Root>
        </DialogContent>
    </Dialog>;
};